// src/encounter/ap-note-dialog.tsx
//
// "Add Text Notes" — the A/P narrative editor, ported from the live Flutter PA
// dialog (Andrew's frame, 2026-08-05): a wide modal with ✕ / centred title /
// Save in the header, a rich-text toolbar, and a tall editing surface.
//
// ─── WHY RICH TEXT IS NOT AN INVENTION HERE ──────────────────────────────────
// The backend already stores this note as a PAIR: `PatientAssessment.notes`
// (a Quill Delta) + `notePlainText` (the flattened text). Flutter authors it
// with `flutter_quill` and reads it back through QuillStaticView. So the toolbar
// is backed — and, more to the point, the plain-text-only editor this replaces
// was a REAL cross-client bug: `src/dashboard/quill-delta.ts` records that a bare
// plaintext string in `notes` renders BLANK in the Flutter client. Every A/P note
// typed in React was invisible to a doctor opening the same encounter in PA.
//
// ─── ENGINE ──────────────────────────────────────────────────────────────────
// Quill (`react-quill-new`), mounted by the Signal DS `RichTextEditor` (DS-I) — Delta-compatible
// with Flutter's `flutter_quill` 10.7.6 (same wire shape, different rendering engine — confirmed
// with Andrew, PROD-764). The DS owns the mount and the `note` skin; this file owns the draft.
//
// ─── DS-FIRST ────────────────────────────────────────────────────────────────
// Chrome is the DS `Modal` (`variant="bleed"`, widened to the frame's 1064px at md+) with
// DS `Button`/`Icon` in a composed header — the DS Modal renders its own `title`
// left-aligned with no action slot, and the frame needs ✕ / centred title / Save,
// so the header is composed into `children` instead. `ModalHeader`/`ModalFooter`
// are QUARANTINED in the DS and deliberately not used.
//
// The editor is the DS `RichTextEditor appearance="note"` — the `.ap-note-quill` toolbar skin
// this file used to scope in index.css graduated into it (2026-09-23).
//
// ─── KNOWN GAPS vs the Flutter toolbar (visible, so stated) ──────────────────
//   • undo / redo buttons — Quill's history module is active (⌘/Ctrl+Z and
//     ⌘/Ctrl+Shift+Z work), but the two toolbar BUTTONS need custom handlers and
//     icons. Omitted rather than rendered dead.
//   • emoji picker — no Quill control and no DS picker.
// Everything else in the frame — font, size, B/I/U/S, colour, highlight, the four
// alignments, block style, ordered/bullet/check lists, link, image — is native
// Quill and present.
//
// ─── SAVED PHRASES AND THEIR PIECES (the frame's "Macro ▾") ──────────────────
// Spec: docs/encounter-alignment/SPEC-smart-phrases-and-template-filling.md §4, §6.
// The provider's phrases (`services/use-note-macros.ts`) are offered by the header's "Macro"
// menu and by `.trigger` typed at the start of a word. A phrase may carry PIECES — pick-lists,
// blanks, and chart links (latest vitals, the allergy list, active problems and medications, a
// catalogue lab set or one LOINC-coded lab result) — which live in the editor as atomic
// embeds (`note-piece-blot.ts`) and on the wire as text runs carrying `cps-piece`
// (`lib/note-pieces.ts`). The codec runs HERE: decode when the dialog opens (a run edited
// elsewhere becomes plain text, with a notice), encode on Save. A phrase writes narrative only:
// never a diagnosis, an order, a charge or a template answer.
//
// Flutter parity note: Flutter's "Macro" dropdown APPENDS the phrase to the end of the note
// (`quill_tool_bar.dart` `appendData`) and its typed triggers are commented out. Here a phrase
// lands at the cursor, which is what a typed trigger needs and what the menu does too when the
// clinician had a cursor in the note; with none, it appends, as Flutter does.

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MutableRefObject } from 'react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Modal,
  ModalYesNo,
  RichTextEditor,
  type RichTextDelta,
  type RichTextOp,
} from '@scripsteam/scrips-signal-ds';
import { X } from 'lucide-react';
import { dedupeMacros, definitionPieceCount, isOfferedIn, matchMacros, phraseLabel, type NoteMacro, type PhraseScope } from './lib/smart-phrase';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import {
  chartOrigins,
  chartScreenText,
  wireMayQuoteChart,
  decodeNoteOps,
  embeddedPiece,
  encodeNoteOps,
  exportPlainText,
  isPieceOpen,
  pieceLabel,
  wallClockStamp,
  type Piece,
} from './lib/note-pieces';
import type { DeltaOp } from './lib/note-delta';
import type { ChartReads } from './lib/to-chart-snapshot';
import { useSmartPhraseEditor, type SmartPhrasePopup } from './smart-phrase-editor';
import { useNoteMacros, type NoteMacroScope } from './services/use-note-macros';
import { DISCARD_TITLE, draftText } from './lib/discard-draft';
import { useLeaveGuard } from './services/use-leave-guard';
import { useUnsavedNote } from './services/unsaved-note';
import { useNoteCardReads, withCardReads } from './services/use-note-card-reads';
import { ChartPieceCard } from './phrase-chart-card';

/** The saved phrases on offer, and whether they have arrived. */
export interface NoteMacroSource {
  status: 'loading' | 'error' | 'ready';
  macros: NoteMacro[];
}

export interface ApNoteDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Current narrative as a Quill Delta ops-array JSON string (the `notes` wire
   * field). Preferred seed — it carries the formatting.
   */
  deltaJson?: string;
  /** Flattened text (`notePlainText`). Seeds the editor when no Delta exists. */
  plainText?: string;
  /** Save. `plain` feeds `notePlainText`, `delta` feeds `notes` (pieces in their wire form). */
  onSave: (plain: string, delta: string) => void;
  /** Header title. The frame says "Add Text Notes" for both add and edit. */
  title?: string;
  /**
   * The saved phrases to offer. Omitted (the app) → the signed-in provider's phrases are fetched
   * when the dialog opens (`useNoteMacros`). Supplied → used as-is (stories, tests).
   */
  macros?: NoteMacroSource;
  /** The page's already-loaded reads that chart pieces quote (`lib/to-chart-snapshot.ts`). */
  chartReads?: ChartReads;
  /** TS-4: the visit's specialty and type — the fetched phrase list is filtered to them server-side. */
  macroScope?: NoteMacroScope;
  /** PROD-3043: the editor's accessible name and placeholder. Default: the A&P narrative's. */
  editorLabel?: string;
  /** PROD-3043: the phrase editors (`cps-phrase.editors`) this note answers to. Default: the A&P narrative. */
  phraseEditors?: readonly string[];
}

/** Quill's document must end in a newline — what `Document.toDelta()` emits. */

/** PROD-2947 Part 2: piece phrases are ON inside frame v2 (no extra switch); with the switch OFF they keep riding
 *  `?ef=phrasePieces`, exactly as before (G6). Read once per open. */
function piecesSwitchedOn(): boolean {
  return isEncounterFlagOn('frameV2') || isEncounterFlagOn('phrasePieces');
}

const EMPTY_OPS: RichTextOp[] = [{ insert: '\n' }];

/** Parse the wire `notes` value into Quill ops. Tolerates both the bare ops array
 *  Flutter writes and a `{ops:[…]}` envelope; anything else is treated as plain text. */
function opsFromWire(deltaJson: string | undefined, plainText: string | undefined): RichTextDelta {
  if (deltaJson?.trim()) {
    try {
      const parsed: unknown = JSON.parse(deltaJson);
      if (Array.isArray(parsed)) return { ops: parsed as RichTextOp[] };
      if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { ops?: unknown[] }).ops)) {
        return { ops: (parsed as { ops: RichTextOp[] }).ops };
      }
    } catch {
      // Not JSON — fall through and treat the string as plain text below.
    }
  }
  const text = plainText?.trim() ? plainText : '';
  return { ops: text ? [{ insert: text.endsWith('\n') ? text : `${text}\n` }] : EMPTY_OPS };
}

/**
 * Toolbar, in the frame's order. Grouped so the rows break the way the frame's do
 * at the dialog's width; Quill lays each group out as a segment with a separator.
 */
const TOOLBAR = [
  [{ font: [] }, { size: [] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
  [{ header: [1, 2, 3, false] }],
  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
  ['link', 'image'],
  ['clean'],
];

type BodyProps = Omit<ApNoteDialogProps, 'open' | 'macros' | 'macroScope'> & {
  /** D-73: the editor writes whether its draft text changed since the dialog opened. */
  changedRef?: MutableRefObject<boolean>;
  /** L8 / F5 · frame v2: phrases by human name, each shown once. Absent → the list as main draws it. */
  named?: boolean;
};

/** The draft id this dialog marks in the unsaved-note store (the in-app leave guard). */
const AP_NOTE_DRAFT_ID = 'ap-note-dialog';

/** Which note this editor is, for the phrase-level `editors` metadata (`lib/smart-phrase.ts`). */
const NOTE_EDITOR = 'ap-narrative';
const NOTE_EDITORS: readonly string[] = [NOTE_EDITOR];

export function ApNoteDialog({ open, macros, macroScope, ...body }: ApNoteDialogProps) {
  // D-73 (design desk, 6 Oct; switch ON and OFF): X, Esc or a click outside asks first when the text changed
  // since the dialog opened; Save never asks. Found live on dev: closing with typed text threw it away. The
  // editor reports whether its draft changed through this ref (the draft itself stays in the editor).
  const changedRef = useRef(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Frame v2 only tonight (the merge precedes the desk stamp, so the switch-OFF page must not change); the OFF
  // prompt follows in its own PR after the stamp. Read once per mount, like the frame itself.
  const [frameV2] = useState(() => isEncounterFlagOn('frameV2'));
  const requestClose = () => {
    if (frameV2 && changedRef.current) setConfirmOpen(true);
    else body.onClose();
  };
  const editorBody = { ...body, onClose: requestClose };
  return (
    <>
    {/* `size="note"` / `maxHeight="note"` = the frame's 1064-wide note dialog capped at 85vh (DS
        Modal steps). `bleed` = the zero-padding body; `scrollBody` makes the popup a clipped flex
        column so the body below can take the fixed height (`h-177.5` = the frame's 710). */}
    <Modal
      open={open}
      onClose={requestClose}
      size="note"
      maxHeight="note"
      variant="bleed"
      scrollBody
      className="h-177.5"
    >
      {/* The body MOUNTS with the dialog, so its draft state initialises from props on mount and
          a discarded draft cannot survive into the next open. That is why there is no
          reset-on-open effect here — remounting is the reset (and `setState` in an effect is
          exactly the cascading-render pattern the lint rule flags). The provider's phrases are
          fetched by that same mount, so a closed dialog costs no request. */}
      {open &&
        (macros ? (
          <NoteEditorBody {...editorBody} changedRef={changedRef} named={frameV2} macroSource={macros} />
        ) : (
          <NoteEditorBodyWithSavedPhrases {...editorBody} changedRef={changedRef} named={frameV2} macroScope={macroScope} />
        ))}
    </Modal>
    {/* "Keep editing" is first in the footer, so it takes the focus when the confirm opens (default focus). */}
    <ModalYesNo
      open={open && confirmOpen}
      title={DISCARD_TITLE}
      message={null}
      cancelLabel="Keep editing"
      confirmLabel="Discard"
      destructive
      onCancel={() => setConfirmOpen(false)}
      onConfirm={() => {
        setConfirmOpen(false);
        changedRef.current = false;
        body.onClose();
      }}
    />
    </>
  );
}

function NoteEditorBodyWithSavedPhrases({ macroScope, ...props }: BodyProps & { macroScope?: NoteMacroScope }) {
  const query = useNoteMacros(macroScope);
  const macroSource: NoteMacroSource = {
    status: query.isError ? 'error' : query.data ? 'ready' : 'loading',
    macros: query.data ?? [],
  };
  // Lab chart links (`labs.set:<id>`, `labs.latest:<LOINC>`) and the value cards' vitals history read
  // the chart's own readers. Fetched only while pieces are on — off, no patient id is passed, nothing
  // is requested, and the page's reads go through untouched. Read once per open, like the editor.
  const [piecesOn] = useState(piecesSwitchedOn);
  const card = useNoteCardReads(piecesOn ? props.chartReads?.patientId || undefined : undefined);
  const chartReads = piecesOn && props.chartReads ? withCardReads(props.chartReads, card) : props.chartReads;
  return <NoteEditorBody {...props} chartReads={chartReads} macroSource={macroSource} />;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * G5 — A NOTE THAT QUOTES THE CHART OPENS ONLY ONCE IT KNOWS WHOSE IT IS. Decoding is where a chart
 * value from another patient or visit is told apart and removed; without this note's own patient and
 * encounter that cannot be decided, and an edited or unreadable chart run decoded then would become
 * plain text with its provenance gone. So the editor (and its one-time decode) waits for both ids —
 * nothing is typed before it mounts, so there is no draft to lose. A note without chart runs opens
 * at once, as before.
 */
function NoteEditorBody(props: BodyProps & { macroSource: NoteMacroSource }) {
  const { deltaJson, plainText, chartReads, onClose } = props;
  const [quotesChart] = useState(() => wireMayQuoteChart(opsFromWire(deltaJson, plainText).ops as DeltaOp[]));
  const identityKnown = !!chartReads?.patientId && !!chartReads?.encounterId;
  // The gate holds the FIRST mount only. Once the editor is open it stays open — an identity that
  // drops out later disables Save through the live guard (`chartOrigins`) instead of unmounting the
  // draft. Recorded while rendering (React's adjust-state pattern), so no frame shows the gate again.
  const [opened, setOpened] = useState(false);
  if (!opened && (!quotesChart || identityKnown)) setOpened(true);
  if (!opened && quotesChart && !identityKnown) {
    return (
      <div className="flex h-full flex-col font-sans">
        <div className="flex shrink-0 items-center gap-2 border-b border-border-subtle px-4 py-3">
          <button type="button" className="icon-action" onClick={onClose} aria-label="Close"><X className="size-4" aria-hidden /></button>
          <h2 className="m-0 text-md font-semibold text-text-primary">{props.title ?? 'Add Text Notes'}</h2>
        </div>
        <p role="status" className="m-0 px-4 py-3 text-sm text-text-secondary">
          This note quotes the chart, so it opens once the visit has loaded.
        </p>
      </div>
    );
  }
  return <NoteEditor {...props} />;
}

function NoteEditor({
  onClose,
  deltaJson,
  plainText,
  onSave,
  title = 'Add Text Notes',
  macroSource,
  chartReads,
  changedRef,
  named = false,
  editorLabel = 'Assessment narrative',
  phraseEditors = NOTE_EDITORS,
}: BodyProps & { macroSource: NoteMacroSource }) {
  // Draft lives here so Close discards — the host only sees text on Save. The wire value is
  // DECODED once, at open: piece runs become embeds; a run edited outside React becomes text; a chart
  // link pulled for another patient or visit (a Flutter copy/paste) is removed, text and all (G5).
  const [initial] = useState(() =>
    decodeNoteOps(opsFromWire(deltaJson, plainText).ops as DeltaOp[], {
      patientId: chartReads?.patientId,
      encounterId: chartReads?.encounterId,
    }),
  );
  const [contents, setContents] = useState<RichTextDelta>(() => ({ ops: initial.ops as RichTextOp[] }));
  // D-73: the draft's text at open, so Close asks only when the text changed (never on an untouched note).
  const [openedText] = useState(() => draftText(initial.ops as RichTextOp[]));
  // D-73 (Codex P1, 6 Oct): the draft lives only here until Save, so closing the TAB with changed text must ask too —
  // the browser's leave-page prompt, frame v2 only tonight (`named` is the frame v2 read).
  const [draftChanged, setDraftChanged] = useState(false);
  useLeaveGuard(named && draftChanged);
  // …and leaving the encounter inside the app asks too (the in-app guard, encounter-leave-guard.tsx). Cleared when the
  // dialog closes (this editor unmounts), so a discarded or saved draft never holds a later navigation.
  useEffect(() => {
    if (!named) return;
    useUnsavedNote.getState().setDraftChanged(AP_NOTE_DRAFT_ID, draftChanged);
    return () => useUnsavedNote.getState().setDraftChanged(AP_NOTE_DRAFT_ID, false);
  }, [named, draftChanged]);
  // Quill's own flattening (`getText()`, reported by the DS editor on mount and on every change),
  // seeded with the stored `notePlainText` — exactly how this dialog fed `notePlainText` before pieces
  // existed. A note with NO pieces still saves this.
  const [plain, setPlain] = useState(plainText ?? '');
  const [menuOpen, setMenuOpen] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  const modules = useMemo(() => ({ toolbar: TOOLBAR, history: { userOnly: true } }), []);
  // PIECES SHIP DARK (`?ef=phrasePieces`, read once per open). Off: a phrase whose definition
  // carries any piece is not offered — in the menu or by `.trigger` — and plain phrases work as
  // before. A note that ALREADY holds pieces still opens with them either way: the codec is not
  // switched, so turning the flag off can never strip another session's pieces on save.
  const [piecesOn] = useState(piecesSwitchedOn);
  const offered = useMemo<NoteMacroSource>(
    () => {
      const macros = macroSource.macros.filter(
        (m) =>
          // Only phrases written for THIS note — a chief-complaint phrase is clinically the wrong
          // content for the A&P narrative (a phrase that names no editor is offered everywhere).
          phraseEditors.some((e) => isOfferedIn(m.value, e)) && (piecesOn || definitionPieceCount(m.value) === 0),
      );
      // L8 / F5 · frame v2: each phrase once (same id, or same name and body).
      return { ...macroSource, macros: named ? dedupeMacros(macros) : macros };
    },
    [macroSource, piecesOn, named, phraseEditors],
  );
  const phrases = useSmartPhraseEditor({ editorRef, macros: offered.macros, chartReads });
  const openPieces = (contents.ops as DeltaOp[]).filter((op) => {
    const p = embeddedPiece(op);
    return p && isPieceOpen(p);
  }).length;

  // G5 — THE SAVE GUARD. The draft belongs to the patient and visit it was opened for — all of it,
  // including chart text that became plain on open. It is BOUND to that identity (adopting ids that
  // arrive after open, which is the same page resolving, not a switch), and Save is refused while
  // the page's identity differs from it or has dropped out. On top, every chart value left in the
  // note is checked against the identity NOW. Live, never frozen at open: when the identity comes
  // back, the guard clears and the draft is still there.
  // An empty id is no id (normalised, so adoption below converges in one step).
  const noteWhere = { patientId: chartReads?.patientId || undefined, encounterId: chartReads?.encounterId || undefined };
  const [boundWhere, setBoundWhere] = useState(noteWhere);
  const adoptable =
    (!boundWhere.patientId || boundWhere.patientId === noteWhere.patientId) &&
    (!boundWhere.encounterId || boundWhere.encounterId === noteWhere.encounterId) &&
    ((!boundWhere.patientId && !!noteWhere.patientId) || (!boundWhere.encounterId && !!noteWhere.encounterId));
  if (adoptable) {
    setBoundWhere({
      patientId: boundWhere.patientId || noteWhere.patientId,
      encounterId: boundWhere.encounterId || noteWhere.encounterId,
    });
  }
  const identityMissing = !noteWhere.patientId || !noteWhere.encounterId;
  const switched =
    !identityMissing && (noteWhere.patientId !== boundWhere.patientId || noteWhere.encounterId !== boundWhere.encounterId);
  const droppedOut = identityMissing && (!!boundWhere.patientId || !!boundWhere.encounterId);
  const blockedFor = (ops: DeltaOp[]) => {
    const check = chartOrigins(ops, noteWhere);
    return { ...check, blocked: switched || droppedOut || check.unchecked > 0 || check.elsewhere > 0 };
  };
  const chartCheck = blockedFor(contents.ops as DeltaOp[]);
  const saveBlocked = chartCheck.blocked;

  const save = () => {
    // A blank's input may still hold an uncommitted edit ("3 days" → "3 weeks", or cleared): it goes
    // into the note FIRST, so Save writes what the clinician sees, never the blank's old value.
    const ops = phrases.commitPending() ?? (contents.ops as DeltaOp[]);
    if (blockedFor(ops).blocked) return;
    const wire = encodeNoteOps(ops);
    // The piece exporter only when the note HOLDS pieces. Without pieces the plain-text half is what
    // it was before pieces existed (Quill's `getText()`), so opening and saving a Flutter-authored note
    // never adds `[x]` / `[ ]` / `[image]` to it.
    const hasPieces = ops.some((op) => embeddedPiece(op) !== undefined);
    const text = hasPieces ? exportPlainText(wire) : plain;
    onSave(text.trim() ? text : '', JSON.stringify(wire));
  };

  return (
    <div className="flex h-full flex-col font-sans">
        {/* Header — ✕ left · title centred · Macro + Save right (the frame's anatomy; the
            DS Modal's own `title` is left-aligned with no action slot). */}
        <div className="flex shrink-0 items-center gap-2 border-b border-border-subtle px-4 py-3">
          {/* Two equal flex-1 sides keep the title centred — the old `1fr auto 1fr` grid. */}
          <span className="flex flex-1 justify-start">
            <button type="button" className="icon-action" onClick={onClose} aria-label="Close"><X className="size-4" aria-hidden /></button>
          </span>
          <h2 className="shrink-0 text-md font-semibold text-text-primary">{title}</h2>
          <span className="flex flex-1 items-center justify-end gap-2">
            <Button
              variant="tertiary"
              size="sm"
              chevron
              aria-expanded={menuOpen}
              aria-controls="ap-note-phrase-menu"
              onClick={() => {
                if (!menuOpen) phrases.captureContract();
                setMenuOpen((o) => !o);
              }}
            >
              Macro
            </Button>
            {/* G5: a chart value that could not be checked against this note's patient and visit is
                never written back — Save waits until the note knows whose it is. */}
            <Button
              variant="tertiary"
              size="sm"
              onClick={save}
              disabled={saveBlocked}
              aria-describedby={saveBlocked ? 'ap-note-save-blocked' : undefined}
            >
              Save
            </Button>
          </span>
        </div>

        {saveBlocked && (
          // Why Save is off — live, so it changes the moment the identity does. A polite live region
          // rather than a second status/alert role: the phrase strip's alert and the piece controls'
          // status keep their own regions.
          <p
            id="ap-note-save-blocked"
            data-testid="note-save-blocked"
            aria-live="polite"
            className="m-0 shrink-0 border-b border-border-subtle bg-status-warning-bg px-4 py-2 text-sm text-status-amber-text"
          >
            {switched
              ? 'This note was opened for a different patient or visit, so it can’t be saved here.'
              : droppedOut || chartCheck.unchecked > 0
                ? 'This note can’t be saved until the visit has loaded.'
                : `${plural(chartCheck.elsewhere, 'chart value', 'chart values')} from another record must be removed before this note can be saved.`}
          </p>
        )}

        {(initial.converted > 0 || initial.removed > 0) && (
          <p role="status" className="m-0 shrink-0 border-b border-border-subtle bg-status-warning-bg px-4 py-2 text-sm text-status-amber-text">
            {initial.removed > 0 &&
              `${plural(initial.removed, 'chart value', 'chart values')} from another record ${initial.removed === 1 ? 'was' : 'were'} removed. `}
            {initial.converted > 0 &&
              `${plural(initial.converted, 'piece', 'pieces')} converted to text — edited outside this editor, so kept as plain narrative.`}
          </p>
        )}

        {phrases.notice && (
          <p role="alert" className="m-0 shrink-0 border-b border-border-subtle bg-status-warning-bg px-4 py-2 text-sm text-status-amber-text">
            {phrases.notice}
          </p>
        )}

        {menuOpen && (
          <PhraseMenu
            named={named}
            source={offered}
            onPick={(macro) => {
              setMenuOpen(false);
              phrases.insertPhrase(macro);
            }}
            onDismiss={() => {
              setMenuOpen(false);
              phrases.focusEditor();
            }}
          />
        )}

        {/* Editor — the DS RichTextEditor in its `note` skin (borderless, a hairline under the
            toolbar, DS-token type and surface). Keys and clipboard are read in the CAPTURE phase
            so the phrase strip, the piece controls and the plain-text paste act before Quill. */}
        <RichTextEditor
          ref={editorRef}
          appearance="note"
          className="min-h-0 flex-1"
          value={contents}
          onChange={(delta, text) => {
            setContents(delta);
            setPlain(text);
            const changed = draftText(delta.ops) !== openedText;
            if (changedRef) changedRef.current = changed;
            setDraftChanged(changed);
          }}
          onKeyDownCapture={phrases.onKeyDownCapture}
          onPasteCapture={phrases.onPasteCapture}
          onCopyCapture={phrases.onCopyCapture}
          onCutCapture={phrases.onCutCapture}
          modules={modules}
          placeholder={`${editorLabel}…`}
          aria-label={editorLabel}
        />

        {phrases.popup?.mode === 'phrase' ? (
          <PhraseStrip named={named} popup={phrases.popup} onHover={phrases.setActive} onChoose={phrases.acceptPhrase} />
        ) : phrases.popup?.mode === 'piece' ? (
          <PiecePanel key={phrases.popup.piece.id} popup={phrases.popup} controls={phrases} cards={piecesOn} />
        ) : (
          <>
            {openPieces > 0 && (
              <p role="status" className="m-0 shrink-0 border-t border-border-subtle px-4 py-2 text-sm text-text-secondary">
                {plural(openPieces, 'piece', 'pieces')} open — F2 moves to the next, Shift+F2 to the previous.
              </p>
            )}
            {piecesOn && <NoteChartCards ops={contents.ops as DeltaOp[]} />}
          </>
        )}
    </div>
  );
}

const SCOPE_LABEL: Record<PhraseScope, string> = { own: 'Mine', shared: 'Shared with me', global: 'Everyone' };

function PhraseRow({ macro, named = false }: { macro: NoteMacro; named?: boolean }) {
  return (
    <span className="flex min-w-0 flex-1 items-baseline gap-2">
      {/* L8 / F5 · frame v2: the phrase's human name ("Copy goal"), never its dotted id (".copy:-goal"). */}
      <span className="shrink-0 font-medium">{named ? phraseLabel(macro) : `.${macro.trigger}`}</span>
      <span className="shrink-0 text-xs text-text-secondary">{SCOPE_LABEL[macro.scope]}</span>
      <span className="truncate text-xs text-text-muted">{macro.preview}</span>
    </span>
  );
}

/** The header "Macro" menu: search the provider's saved phrases and insert one at the cursor. */
function PhraseMenu({
  source,
  onPick,
  onDismiss,
  named = false,
}: {
  source: NoteMacroSource;
  onPick: (macro: NoteMacro) => void;
  onDismiss: () => void;
  named?: boolean;
}) {
  const [search, setSearch] = useState('');
  const items = matchMacros(source.macros, search, 50);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Esc closes the menu, not the dialog.
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onDismiss();
    }
  };
  return (
    <div id="ap-note-phrase-menu" className="shrink-0 border-b border-border-subtle px-4 py-2" onKeyDown={onKeyDown}>
      <Command shouldFilter={false} label="Saved phrases">
        <CommandInput placeholder="Search saved phrases…" value={search} onValueChange={setSearch} autoFocus />
        <CommandList>
          {source.status === 'loading' ? (
            <CommandEmpty>Loading saved phrases…</CommandEmpty>
          ) : source.status === 'error' ? (
            <CommandEmpty>Couldn’t load saved phrases.</CommandEmpty>
          ) : items.length === 0 ? (
            <CommandEmpty>{source.macros.length ? 'No saved phrase matches.' : 'No saved phrases yet.'}</CommandEmpty>
          ) : (
            <CommandGroup>
              {items.map((m) => (
                <CommandItem key={m.id} value={m.id} onSelect={() => onPick(m)}>
                  <PhraseRow macro={m} named={named} />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </div>
  );
}

/**
 * The strip under the editor while a `.trigger` is being typed. It never takes focus — the
 * clinician keeps typing, and the editor's capture-phase keys drive ↑ ↓, Enter / Tab and Esc.
 */
function PhraseStrip({
  popup,
  onHover,
  onChoose,
  named = false,
}: {
  named?: boolean;
  popup: Extract<SmartPhrasePopup, { mode: 'phrase' }>;
  onHover: (i: number) => void;
  onChoose: (i: number) => void;
}) {
  return (
    <div className="shrink-0 border-t border-border-subtle px-4 py-2">
      <p className="m-0 pb-1 text-xs text-text-secondary">
        Saved phrases for “.{popup.trigger.query}” — ↑ ↓ to choose, Enter to insert, Esc to keep typing
      </p>
      <Command
        shouldFilter={false}
        label="Matching saved phrases"
        value={popup.items[popup.active]?.id ?? ''}
        onValueChange={(v) => {
          const i = popup.items.findIndex((m) => m.id === v);
          if (i >= 0) onHover(i);
        }}
      >
        <CommandList>
          <CommandGroup>
            {popup.items.map((m, i) => (
              <CommandItem
                key={m.id}
                value={m.id}
                // Keep the caret in the note: a click picks, it does not move focus.
                onMouseDown={(e) => e.preventDefault()}
                onSelect={() => onChoose(i)}
              >
                <PhraseRow macro={m} named={named} />
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
}

type Controls = ReturnType<typeof useSmartPhraseEditor>;

/**
 * Value cards for every chart piece in the note, under the editor while nothing is selected
 * (`phrasePieces` on). Each card draws its piece's frozen snapshot; to refresh one or make it plain
 * text, the clinician selects its chip, which opens the piece panel.
 */
function NoteChartCards({ ops }: { ops: DeltaOp[] }) {
  const charts = ops.map((op) => embeddedPiece(op)).filter((p): p is Extract<Piece, { t: 'chart' }> => p?.t === 'chart');
  if (!charts.length) return null;
  return (
    <section
      aria-label="Values quoted from the chart"
      data-testid="note-chart-cards"
      className="flex max-h-72 shrink-0 flex-col gap-3 overflow-y-auto border-t border-border-subtle px-4 py-2"
    >
      {charts.map((piece) => (
        <ChartPieceCard key={piece.id} piece={piece} />
      ))}
      <p className="m-0 text-xs text-text-muted">Select a chart value in the note to refresh it or make it plain text.</p>
    </section>
  );
}

/** The control for the selected piece (R2: a piece changes only through here). */
function PiecePanel({ popup, controls, cards = false }: { popup: Extract<SmartPhrasePopup, { mode: 'piece' }>; controls: Controls; cards?: boolean }) {
  const piece = popup.piece;
  const keepCaret = (e: React.MouseEvent) => e.preventDefault();
  return (
    <div className="flex shrink-0 flex-col gap-2 border-t border-border-subtle px-4 py-2" aria-label={`Piece: ${pieceLabel(piece)}`}>
      {piece.t === 'pick' && <PickControl piece={piece} active={popup.active} controls={controls} keepCaret={keepCaret} />}
      {piece.t === 'blank' && <BlankControl piece={piece} controls={controls} />}
      {piece.t === 'chart' && <ChartControl piece={piece} controls={controls} keepCaret={keepCaret} />}
      {/* With the value cards on, the selected chart piece also shows its card: the values it quotes,
          frozen — and, after a Refresh that found newer values, marked stale until Update. */}
      {piece.t === 'chart' && cards && (
        <div className="max-h-60 overflow-y-auto">
          <ChartPieceCard piece={piece} stale={controls.refreshState?.kind === 'changed'} />
        </div>
      )}
      <span className="flex items-center gap-2">
        <Button variant="tertiary" size="sm" onMouseDown={keepCaret} onClick={() => controls.makePlainText()}>
          Make plain text
        </Button>
        <span className="text-xs text-text-muted">F2 next piece · Shift+F2 previous · Esc back to the note</span>
      </span>
    </div>
  );
}

function PickControl({
  piece,
  active,
  controls,
  keepCaret,
}: {
  piece: Extract<Piece, { t: 'pick' }>;
  active: number;
  controls: Controls;
  keepCaret: (e: React.MouseEvent) => void;
}) {
  const chosen = new Set(piece.chosen ?? []);
  const [other, setOther] = useState('');
  return (
    <>
      <p className="m-0 text-xs text-text-secondary">
        {piece.multi ? `Choose any — ${piece.label}. Space toggles, Enter when done.` : `Choose one — ${piece.label}.`} 1–9 or ↑ ↓.
        {!chosen.size && ' Not chosen yet.'}
      </p>
      <ul role="listbox" aria-label={piece.label} aria-multiselectable={piece.multi || undefined} className="m-0 flex list-none flex-wrap gap-2 p-0">
        {piece.options.map((o, i) => (
          <li key={o.code} role="option" aria-selected={chosen.has(o.code)}>
            <Button
              variant={chosen.has(o.code) ? 'primary' : i === active ? 'secondary' : 'neutral'}
              size="sm"
              onMouseDown={keepCaret}
              onClick={() => controls.choose(o.code)}
            >
              {i < 9 ? `${i + 1}. ` : ''}
              {o.display}
            </Button>
          </li>
        ))}
      </ul>
      {piece.other && (
        <span className="flex items-center gap-2">
          <input
            aria-label={`Other ${piece.label}`}
            placeholder="Other… (free text, not coded)"
            className="h-8 flex-1 rounded-md border border-border-default bg-surface-card px-2 text-sm"
            value={other}
            onChange={(e) => setOther(e.target.value)}
          />
          <Button variant="secondary" size="sm" disabled={!other.trim()} onClick={() => controls.makePlainText(other)}>
            Use as text
          </Button>
        </span>
      )}
    </>
  );
}

function BlankControl({ piece, controls }: { piece: Extract<Piece, { t: 'blank' }>; controls: Controls }) {
  const [value, setValue] = useState(piece.value ?? '');
  // Every keystroke is handed to the editor as this blank's PENDING value, so Save, F2, a click on
  // another piece or Esc commit it — the edit never lives only in this input's state.
  const change = (next: string) => {
    setValue(next);
    controls.draftBlank(piece.id, next);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // The caret is in this input, not the editor, so the piece keys are handled here too:
    // Enter fills and goes on to the next open piece; Tab / F2 move next, Shift+Tab / Shift+F2
    // previous — each keeping what was typed. An emptied input commits as a clear.
    if (e.key === 'Enter') {
      e.preventDefault();
      controls.fill(value, value.trim() ? 'advance' : 'next');
    } else if (e.key === 'Tab' || e.key === 'F2') {
      e.preventDefault();
      e.stopPropagation();
      controls.fill(value, e.shiftKey ? 'previous' : 'next');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      controls.closePiece();
    }
  };
  return (
    <span className="flex flex-wrap items-center gap-2">
      <input
        aria-label={piece.label}
        placeholder={piece.label}
        // Typing fills the blank: F2 onto a blank puts the caret here.
        autoFocus
        className="h-8 min-w-60 flex-1 rounded-md border border-border-default bg-surface-card px-2 text-sm"
        value={value}
        onChange={(e) => change(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={controls.commitBlank}
      />
      {piece.unit && <span className="text-sm text-text-secondary">{piece.unit}</span>}
      <Button variant="secondary" size="sm" disabled={!value.trim()} onClick={() => controls.fill(value)}>
        Fill
      </Button>
      <Button variant="tertiary" size="sm" onClick={() => controls.fill('unknown')}>
        unknown
      </Button>
      <Button variant="tertiary" size="sm" onClick={() => controls.fill('not applicable')}>
        not applicable
      </Button>
    </span>
  );
}

function ChartControl({
  piece,
  controls,
  keepCaret,
}: {
  piece: Extract<Piece, { t: 'chart' }>;
  controls: Controls;
  keepCaret: (e: React.MouseEvent) => void;
}) {
  const pulled = wallClockStamp(piece.snapshot?.pulledAt);
  const state = controls.refreshState;
  return (
    <span className="flex flex-col gap-2">
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-text-primary">{chartScreenText(piece)}</span>
        <span className="text-xs text-text-muted">{pulled ? `Pulled ${pulled} — not updated automatically.` : 'Not pulled yet.'}</span>
        <Button
          variant="secondary"
          size="sm"
          onMouseDown={keepCaret}
          onClick={() => void controls.refresh()}
          disabled={state?.kind === 'checking'}
        >
          {state?.kind === 'checking' ? 'Checking…' : 'Refresh'}
        </Button>
      </span>
      {state && state.kind !== 'checking' && (
        <span role="status" className="flex flex-wrap items-center gap-2 text-sm">
          {state.kind === 'unchanged' && <span className="text-text-secondary">Unchanged as of {wallClockStamp(state.at)}.</span>}
          {state.kind === 'refused' && (
            <span className="text-status-amber-text">
              This value was pulled for a different patient or encounter, so it is not refreshed here.
            </span>
          )}
          {state.kind === 'failed' && <span className="text-status-amber-text">Couldn’t re-read the chart — the note is unchanged.</span>}
          {state.kind === 'changed' && (
            <>
              <span className="text-text-primary">
                Changed: <s>{chartScreenText(piece)}</s> → {chartScreenText({ ...piece, snapshot: state.next })}
              </span>
              <Button variant="primary" size="sm" onMouseDown={keepCaret} onClick={controls.applyRefresh}>
                Update
              </Button>
            </>
          )}
        </span>
      )}
    </span>
  );
}
